import { Button, Modal } from "antd";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import LabeledInput from "components/Form/LabeledInput";
import LabeledUploadInput from "components/Form/LabeledUploadInput";
import { useNotificationContext } from "contexts/NotificationContext";
import { IUser, IUpdatePassenger } from "models/user";
import updatePassengerValidator from "validators/updatePassenger.validator";
import { updatePassengerByIdApi } from "api/passenger";
import { uploadPassengerApi } from "api/upload";
import { useState, useEffect } from "react";
import { RcFile } from "antd/es/upload";

interface IProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
  user: IUser;
}

interface IEditForm extends Omit<IUpdatePassenger, "image"> {
  image?: any;
}

export default function EditPassengerModal({ visible, onCancel, onSuccess, user }: IProps) {
  const { showSuccess, showError } = useNotificationContext();
  const [loading, setLoading] = useState(false);

  const { control, handleSubmit, reset } = useForm<IEditForm>({
    resolver: yupResolver(updatePassengerValidator) as any,
    defaultValues: {
      first_name: user.first_name,
      last_name: user.last_name,
      phone: user.phone,
      email: user.email,
      image: user.image ? [{ url: user.image, name: "Profile Image" }] : [],
    },
  });

  useEffect(() => {
    if (visible) {
      reset({
        first_name: user.first_name,
        last_name: user.last_name,
        phone: user.phone,
        email: user.email,
        image: user.image ? [{ url: user.image, name: "Profile Image" }] : [],
      });
    }
  }, [visible, user, reset]);

  const handleUploadImage = async (file: RcFile) => {
    try {
      const { data, error } = await uploadPassengerApi(file as any);
      if (data && data.data) return data.data.url;
      showError(error?.message || "Failed to upload image");
    } catch (err) {
      showError("Image upload failed");
    }
  };

  const onSubmit = async (values: IEditForm) => {
    setLoading(true);
    try {
      const submitData: IUpdatePassenger = {
        first_name: values.first_name,
        last_name: values.last_name,
        phone: values.phone,
        email: values.email,
      };

      if (values.image && Array.isArray(values.image) && values.image.length > 0) {
        const file = values.image[0];
        submitData.image = file.url || file.response?.data?.url || file.response?.url || "";
      } else if (typeof values.image === "string") {
        submitData.image = values.image;
      }

      const { data: response, error } = await updatePassengerByIdApi(user.id, submitData);
      if (response) {
        showSuccess("Passenger updated successfully");
        onSuccess();
      } else {
        showError(error?.message || "Failed to update passenger");
      }
    } catch (err) {
      showError("An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Edit Passenger"
      open={visible}
      onCancel={onCancel}
      footer={null}
      centered
      width={700}
    >
      <form onSubmit={handleSubmit(onSubmit)} className="w-full flex flex-wrap pt-4">
        <div className="w-full md:w-1/2 px-2 pb-4">
          <LabeledInput
            label="First Name"
            name="first_name"
            type="text"
            control={control}
            placeholder="Enter first name"
          />
        </div>
        <div className="w-full md:w-1/2 px-2 pb-4">
          <LabeledInput
            label="Last Name"
            name="last_name"
            type="text"
            control={control}
            placeholder="Enter last name"
          />
        </div>
        <div className="w-full md:w-1/2 px-2 pb-4">
          <LabeledInput
            label="Phone Number"
            name="phone"
            type="text"
            control={control}
            placeholder="Enter phone number"
          />
        </div>
        <div className="w-full md:w-1/2 px-2 pb-4">
          <LabeledInput
            label="Email (Optional)"
            name="email"
            type="email"
            control={control}
            placeholder="Enter email"
          />
        </div>
        <div className="w-full px-2 pb-4">
          <LabeledUploadInput
            label="Profile Photo"
            name="image"
            control={control}
            action={handleUploadImage}
            placeholder="Upload Profile Photo"
          />
        </div>
        <div className="w-full flex justify-end gap-x-3 pt-6 px-2">
          <Button onClick={onCancel}>Cancel</Button>
          <Button type="primary" htmlType="submit" loading={loading}>
            Update Passenger
          </Button>
        </div>
      </form>
    </Modal>
  );
}
